"use client";

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import type { Transition } from "motion/react";
import React from "react";
import { splitBreakpointProps } from "@/lib/utils";
import type { DropdownMenuContextType, DropdownMenuProps } from "../types";

const DEFAULT_TRANSITION: Transition = {
  type: "spring",
  stiffness: 350,
  damping: 35,
};

export const DropdownMenuContext = React.createContext<
  DropdownMenuContextType | undefined
>(undefined);

export const useDropdownMenu = (): DropdownMenuContextType => {
  const context = React.useContext(DropdownMenuContext);

  if (!context) {
    throw new Error("useDropdownMenu must be used within a DropdownMenu");
  }

  return context;
};

function DropdownMenuRoot({
  transition = DEFAULT_TRANSITION,
  animateOnHover = true,
  children,
  open,
  onOpenChange,
  defaultOpen,
  ...props
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = React.useState(open ?? defaultOpen ?? false);

  const { native } = splitBreakpointProps(props);

  React.useEffect(() => {
    if (open === undefined) {
      return;
    }

    setIsOpen(open);
  }, [open]);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  const context = React.useMemo(
    () => ({
      isOpen,
      highlightTransition: transition,
      animateOnHover,
    }),
    [isOpen, transition, animateOnHover],
  );

  return (
    <DropdownMenuContext.Provider value={context}>
      <DropdownMenuPrimitive.Root
        data-slot="dropdown-menu"
        {...native}
        onOpenChange={handleOnOpenChange}
      >
        {children}
      </DropdownMenuPrimitive.Root>
    </DropdownMenuContext.Provider>
  );
}

DropdownMenuRoot.displayName = "DropdownMenu";

export default DropdownMenuRoot;
